import type {
	AdminScannerModel,
	GetModelEvaluationsResponse,
	ListSignatureRunsResponse,
} from "@repo/marketplace-admin"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import {
	ArrowLeft,
	Cpu,
	Database,
	ExternalLink,
	Fingerprint,
	FlaskConical,
	Gauge,
	History,
	Images,
	Package,
	Tags,
	Target,
	TriangleAlert,
} from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	FLUSH_GRID_CLASS,
	FLUSH_INNER_PANEL_CLASS,
	FLUSH_TABLE_CLASS,
	RawReportPanel,
	SampleGrid,
	SCANNER_DETAIL_PAGE_CLASS,
	ScannerDetailSplit,
	WarningsPanel,
	WRAPPING_HEADER_ACTIONS,
} from "@/features/card-scanner/detail-blocks"
import { EvaluateDialog } from "@/features/card-scanner/eval-actions"
import {
	headlineMetric,
	VerdictBadge,
} from "@/features/card-scanner/eval-shared"
import {
	formatBytes,
	formatMetric,
	formatSeconds,
	MetricDelta,
	MetricGrid,
	numericMetrics,
} from "@/features/card-scanner/metrics"
import {
	PrepareButton,
	ToggleLiveDialog,
} from "@/features/card-scanner/model-actions"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	Instant,
	MODEL_TYPE_LABELS,
	MODEL_TYPE_ROLES,
	QueryError,
	StatusBadge,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/card-scanner/models_/$modelId")({
	component: ModelDetailPage,
})

type RunRow = ListSignatureRunsResponse["runs"][number]

// ============================================================================
// PAGE
// ============================================================================

function ModelDetailPage() {
	const allowed = useScannerGate()
	const { modelId } = Route.useParams()
	const { orpc, isAuthReady } = useMarketplaceOrpc()
	const [toggleOpen, setToggleOpen] = React.useState(false)
	const [evaluateOpen, setEvaluateOpen] = React.useState(false)

	const detailQuery = useQuery({
		...orpc.cardScanner.getModel.queryOptions({ input: { modelId } }),
		enabled: allowed && isAuthReady,
	})
	const model = detailQuery.data?.model
	const loadedModelType = model?.type
	const loadedModelId = model?.id
	const evaluateTarget = React.useMemo(
		() =>
			loadedModelType && loadedModelId
				? { role: MODEL_TYPE_ROLES[loadedModelType], modelId: loadedModelId }
				: undefined,
		[loadedModelType, loadedModelId],
	)

	const versionsQuery = useQuery({
		...orpc.cardScanner.listModels.queryOptions({
			input: { type: model?.type },
		}),
		enabled: allowed && isAuthReady && !!model,
	})

	const jobQuery = useQuery({
		...orpc.cardScanner.getTrainingJob.queryOptions({
			input: { jobId: model?.sourceJobId ?? "" },
		}),
		enabled: allowed && isAuthReady && !!model?.sourceJobId,
	})

	const runsQuery = useQuery({
		...orpc.cardScanner.listSignatureRuns.queryOptions({
			input: { modelId },
		}),
		enabled: allowed && isAuthReady && !!detailQuery.data?.coverage,
	})

	const evaluationsQuery = useQuery({
		...orpc.cardScanner.getModelEvaluations.queryOptions({
			input: { modelId },
		}),
		enabled: allowed && isAuthReady && !!model,
	})

	if (!allowed) return null

	if (detailQuery.isLoading) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<Skeleton className="h-10 w-96" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}
	if (detailQuery.isError || !model) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<BackLink />
				<QueryError
					what="the model"
					error={detailQuery.error ?? new Error("Model not found")}
				/>
			</div>
		)
	}

	const coverage = detailQuery.data?.coverage ?? null
	const sourceJob = detailQuery.data?.sourceJob ?? null
	const summary = model.reportSummary
	const versions = (versionsQuery.data?.models ?? [])
		.filter((entry) => entry.type === model.type)
		.sort((a, b) => a.version - b.version)
	const previous = [...versions]
		.reverse()
		.find((entry) => entry.version < model.version)
	const currentMetrics = numericMetrics(model.metrics)
	const previousMetrics = numericMetrics(previous?.metrics)

	return (
		<div className={SCANNER_DETAIL_PAGE_CLASS}>
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					className={WRAPPING_HEADER_ACTIONS}
					title={
						<span className="inline-flex items-center gap-3">
							{MODEL_TYPE_LABELS[model.type]} v{model.version}
							{model.live && (
								<StatusDot tone="ok" className="font-normal text-sm">
									live
								</StatusDot>
							)}
						</span>
					}
					description={
						<span className="flex flex-wrap items-center gap-x-4 gap-y-1">
							<CopyChip label="checksum" value={model.checksum} />
							{model.fingerprint && (
								<CopyChip label="fingerprint" value={model.fingerprint} />
							)}
						</span>
					}
					actions={
						<>
							{coverage && !model.live && <PrepareButton model={model} />}
							<Button variant="outline" onClick={() => setEvaluateOpen(true)}>
								Evaluate
							</Button>
							<Button
								variant={model.live ? "outline" : "default"}
								onClick={() => setToggleOpen(true)}
							>
								{model.live ? "Demote" : "Promote"}
							</Button>
						</>
					}
				/>
			</div>

			<ScannerDetailSplit
				aside={
					<DetailPanel title="Model" icon={Cpu}>
						{(summary?.seconds !== undefined ||
							summary?.environment?.gpu ||
							summary?.git?.commit ||
							summary?.schemaVersion !== undefined) && (
							<DetailPanelSection label="Training">
								<KeyValueList>
									{summary?.seconds !== undefined && (
										<KeyValueItem label="Trained in">
											{formatSeconds(summary.seconds)}
										</KeyValueItem>
									)}
									{summary?.environment?.gpu && (
										<KeyValueItem label="GPU">
											{summary.environment.gpu}
										</KeyValueItem>
									)}
									{summary?.git?.commit && (
										<KeyValueItem label="Commit">
											<span className="font-mono text-xs">
												{summary.git.commit.slice(0, 7)}
											</span>
											{summary.git.dirty && (
												<span className="text-warn"> · dirty</span>
											)}
										</KeyValueItem>
									)}
									{summary?.schemaVersion !== undefined && (
										<KeyValueItem label="Report">
											v{summary.schemaVersion}
										</KeyValueItem>
									)}
								</KeyValueList>
							</DetailPanelSection>
						)}
						<DetailPanelSection label="History">
							<KeyValueList>
								<KeyValueItem label="Created">
									<Instant iso={model.createdAt} />
								</KeyValueItem>
								{model.promotedAt && (
									<KeyValueItem
										label={model.live ? "Promoted" : "Last toggled"}
									>
										<Instant iso={model.promotedAt} />
										{model.promotedBy && (
											<span className="text-muted-foreground">
												{" "}
												· {model.promotedBy}
											</span>
										)}
									</KeyValueItem>
								)}
								{sourceJob && (
									<KeyValueItem label="Source job">
										<Link
											to="/card-scanner/jobs/$jobId"
											params={{ jobId: sourceJob.id }}
											className="font-mono text-xs hover:text-primary-text hover:underline underline-offset-2"
										>
											{sourceJob.id.slice(0, 8)}…
										</Link>
									</KeyValueItem>
								)}
							</KeyValueList>
						</DetailPanelSection>
					</DetailPanel>
				}
			>
				{summary?.warnings && summary.warnings.length > 0 && (
					<WarningsPanel
						noun="training"
						warnings={summary.warnings}
						icon={TriangleAlert}
					/>
				)}

				<DetailPanel
					title="Headline metrics"
					icon={Gauge}
					meta={
						previous
							? `Change vs v${previous.version}`
							: "No previous version to compare against"
					}
					flush
				>
					{Object.keys(currentMetrics).length === 0 ? (
						<DetailPanelSection>
							<Empty>No metrics were reported for this model.</Empty>
						</DetailPanelSection>
					) : (
						<MetricGrid
							metrics={currentMetrics}
							previous={previousMetrics}
							className={FLUSH_INNER_PANEL_CLASS}
						/>
					)}
				</DetailPanel>

				<DetailPanel title="Metric history" icon={History} flush>
					<MetricHistory versions={versions} currentId={model.id} />
				</DetailPanel>

				<DetailPanel
					title="Evaluations"
					icon={FlaskConical}
					meta="Suite version by set; verdicts against the live model on the same data"
					flush
				>
					{evaluationsQuery.isLoading ? (
						<DetailPanelSection>
							<Skeleton className="h-24 w-full" />
						</DetailPanelSection>
					) : (
						<EvaluationMatrix
							rows={evaluationsQuery.data?.rows ?? []}
							modelId={model.id}
							evalSummary={model.evalSummary}
						/>
					)}
				</DetailPanel>

				{summary?.benchmarks && (
					<DetailPanel title="Benchmarks" icon={Target} flush>
						<BenchmarkTable
							benchmarks={summary.benchmarks}
							previous={previous?.reportSummary?.benchmarks}
							previousVersion={previous?.version}
						/>
					</DetailPanel>
				)}

				{summary?.inputs && (
					<DetailPanel title="Training inputs" icon={Database} flush>
						<InputsTable
							inputs={summary.inputs}
							previous={previous?.reportSummary?.inputs}
						/>
					</DetailPanel>
				)}

				{coverage && (
					<DetailPanel
						title="Signature coverage"
						icon={Fingerprint}
						meta={
							<>
								{coverage.doneGames.length}/{coverage.expectedGames.length}{" "}
								games have a database for this model
							</>
						}
						flush
					>
						<DetailPanelSection className="border-b">
							{coverage.missingGames.length > 0 ? (
								<div className="flex flex-wrap items-center gap-1">
									<span className="mr-1 text-muted-foreground">Missing</span>
									{coverage.missingGames.map((game) => (
										<Badge
											key={game}
											variant="warn"
											className="font-mono font-normal"
										>
											{game}
										</Badge>
									))}
								</div>
							) : (
								<StatusDot tone="ok">
									Full coverage: every expected game has a signature database.
								</StatusDot>
							)}
						</DetailPanelSection>
						{runsQuery.data && runsQuery.data.runs.length > 0 && (
							<div className={FLUSH_GRID_CLASS}>
								<SignatureRunsMiniGrid runs={runsQuery.data.runs} />
							</div>
						)}
					</DetailPanel>
				)}

				<DetailPanel
					title="Artifacts"
					icon={Package}
					meta="Internal bucket"
					flush
				>
					<Table className={FLUSH_TABLE_CLASS}>
						<TableBody>
							{Object.entries(model.artifacts).map(([format, ref]) => (
								<TableRow key={format}>
									<TableCell className="font-medium font-mono text-xs uppercase">
										{format}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{ref.key.split("/").at(-1)}
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{formatBytes(ref.size)}
									</TableCell>
									<TableCell className="text-right">
										<CopyChip label="sha256" value={ref.sha256} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</DetailPanel>

				{model.labelsSnapshot && model.labelsSnapshot.length > 0 && (
					<DetailPanel
						title="Labels snapshot"
						icon={Tags}
						meta="Training class order"
					>
						<DetailPanelSection>
							<div className="flex flex-wrap gap-1">
								{model.labelsSnapshot.map((label, index) => (
									<Badge
										key={label}
										variant="neutral"
										className="font-mono font-normal"
									>
										{index}·{label}
									</Badge>
								))}
							</div>
						</DetailPanelSection>
					</DetailPanel>
				)}

				{jobQuery.data && (
					<DetailPanel title="Eval samples" icon={Images}>
						<DetailPanelSection>
							{jobQuery.data.sampleUrls.length === 0 ? (
								<Empty>
									No samples uploaded, or the bucket is unreachable from this
									environment.
								</Empty>
							) : (
								<SampleGrid samples={jobQuery.data.sampleUrls} />
							)}
						</DetailPanelSection>
					</DetailPanel>
				)}

				{jobQuery.data?.resultMetadata && (
					<RawReportPanel
						title="Raw run report"
						data={jobQuery.data.resultMetadata}
						actions={
							jobQuery.data.logUrl && (
								<a
									href={jobQuery.data.logUrl}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
									onClick={(event) => event.stopPropagation()}
								>
									training log <ExternalLink className="h-3 w-3" />
								</a>
							)
						}
					/>
				)}
			</ScannerDetailSplit>

			{toggleOpen && (
				<ToggleLiveDialog model={model} onClose={() => setToggleOpen(false)} />
			)}

			<EvaluateDialog
				open={evaluateOpen}
				onClose={() => setEvaluateOpen(false)}
				initialTarget={evaluateTarget}
			/>
		</div>
	)
}

type EvaluationRow = GetModelEvaluationsResponse["rows"][number]

/** Suite version × set matrix of every result this model was a target in. */
function EvaluationMatrix({
	rows,
	modelId,
	evalSummary,
}: {
	rows: EvaluationRow[]
	modelId: string
	evalSummary: Record<string, number> | null
}) {
	const navigate = useNavigate()
	const targetRows = rows.filter((row) => row.comparison?.modelId === modelId)
	if (targetRows.length === 0) {
		return (
			<DetailPanelSection>
				<Empty>
					No evaluation has targeted this model yet. Dispatch one with Evaluate.
				</Empty>
			</DetailPanelSection>
		)
	}

	const suiteVersions = [
		...new Set(targetRows.map((row) => row.suiteVersion)),
	].sort((a, b) => b - a)
	const setNames = [
		...new Set(targetRows.map((row) => row.result.setName)),
	].sort()
	const cell = (suiteVersion: number, setName: string) =>
		targetRows.find(
			(row) =>
				row.suiteVersion === suiteVersion && row.result.setName === setName,
		)
	const latest = targetRows[0]

	const hasSummary = !!evalSummary && Object.keys(evalSummary).length > 0
	const unmeasured = latest?.result.unmeasuredGames ?? []

	return (
		<>
			<Table className={FLUSH_TABLE_CLASS}>
				<TableHeader>
					<TableRow>
						<TableHead>Suite</TableHead>
						{setNames.map((setName) => (
							<TableHead key={setName} className="text-right font-mono">
								{setName}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{suiteVersions.map((suiteVersion) => (
						<TableRow key={suiteVersion}>
							<TableCell className="font-medium tabular-nums">
								v{suiteVersion}
							</TableCell>
							{setNames.map((setName) => {
								const row = cell(suiteVersion, setName)
								const headline = row ? headlineMetric(row.result.overall) : null
								return (
									<TableCell key={setName} className="text-right">
										{row ? (
											<button
												type="button"
												className="inline-flex items-center gap-1.5 tabular-nums hover:text-primary-text"
												onClick={() =>
													navigate({
														to: "/card-scanner/eval-jobs/$jobId",
														params: { jobId: row.result.evalJobId },
													})
												}
											>
												{headline
													? formatMetric(headline.key, headline.value)
													: "—"}
												{row.comparison && (
													<VerdictBadge
														verdict={row.comparison.verdict}
														small
													/>
												)}
											</button>
										) : (
											<span className="text-meta">—</span>
										)}
									</TableCell>
								)
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>

			{(hasSummary || unmeasured.length > 0) && (
				<DetailPanelSection label="Summary" className="border-t">
					{hasSummary && (
						<div className="flex flex-wrap gap-1">
							{Object.entries(evalSummary).map(([key, value]) => (
								<Badge
									key={key}
									variant="neutral"
									className="font-mono font-normal"
								>
									{key} {formatMetric(key, value)}
								</Badge>
							))}
						</div>
					)}

					{unmeasured.length > 0 && (
						<p className="text-warn text-xs">
							No eval examples yet for:{" "}
							<span className="font-mono">{unmeasured.join(", ")}</span>
						</p>
					)}
				</DetailPanelSection>
			)}
		</>
	)
}

// ============================================================================
// SECTIONS
// ============================================================================

function BackLink() {
	return (
		<Link
			to="/card-scanner/models"
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" /> Models
		</Link>
	)
}

function Empty({ children }: { children: React.ReactNode }) {
	return <p className="text-muted-foreground">{children}</p>
}

function CopyChip({ label, value }: { label: string; value: string }) {
	return (
		<button
			type="button"
			className="max-w-56 truncate font-mono text-muted-foreground text-xs hover:text-primary-text"
			title={`${value} (click to copy)`}
			onClick={() => {
				navigator.clipboard
					.writeText(value)
					.then(() => toast.success(`${label} copied`))
					.catch(() => toast.error(`Could not copy ${label}`))
			}}
		>
			{value}
		</button>
	)
}

/** Metrics across the type's versions (latest 6), current column highlighted. */
function MetricHistory({
	versions,
	currentId,
}: {
	versions: AdminScannerModel[]
	currentId: string
}) {
	const shown = versions.slice(-6)
	const keys = [
		...new Set(
			shown.flatMap((version) => Object.keys(numericMetrics(version.metrics))),
		),
	].sort()

	if (keys.length === 0 || shown.length < 2) {
		return (
			<DetailPanelSection>
				<Empty>
					Metric history appears once at least two versions of this type have
					reported metrics.
				</Empty>
			</DetailPanelSection>
		)
	}

	return (
		<Table className={FLUSH_TABLE_CLASS}>
			<TableHeader>
				<TableRow>
					<TableHead>Metric</TableHead>
					{shown.map((version) => (
						<TableHead
							key={version.id}
							className={cn(
								"text-right tabular-nums",
								version.id === currentId && "font-semibold text-foreground",
							)}
						>
							v{version.version}
							{version.live && " ●"}
						</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{keys.map((key) => (
					<TableRow key={key}>
						<TableCell className="max-w-48 truncate text-muted-foreground">
							{key}
						</TableCell>
						{shown.map((version) => {
							const value = numericMetrics(version.metrics)[key]
							return (
								<TableCell
									key={version.id}
									className={cn(
										"text-right tabular-nums",
										version.id === currentId && "font-semibold",
										value === undefined && "text-meta",
									)}
								>
									{value === undefined ? "—" : formatMetric(key, value)}
								</TableCell>
							)
						})}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function BenchmarkTable({
	benchmarks,
	previous,
	previousVersion,
}: {
	benchmarks: Record<string, Record<string, number>>
	previous: Record<string, Record<string, number>> | undefined
	previousVersion: number | undefined
}) {
	const rows = Object.entries(benchmarks).flatMap(([set, metrics]) =>
		Object.entries(metrics).map(([key, value]) => ({ set, key, value })),
	)
	return (
		<Table className={FLUSH_TABLE_CLASS}>
			<TableHeader>
				<TableRow>
					<TableHead>Benchmark</TableHead>
					<TableHead>Metric</TableHead>
					<TableHead className="text-right">Value</TableHead>
					<TableHead className="text-right">
						{previousVersion !== undefined ? `Δ v${previousVersion}` : ""}
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map(({ set, key, value }, index) => (
					<TableRow key={`${set}.${key}`}>
						<TableCell className="font-mono text-xs">
							{index === 0 || rows[index - 1]?.set !== set ? set : ""}
						</TableCell>
						<TableCell className="text-muted-foreground">{key}</TableCell>
						<TableCell className="text-right tabular-nums">
							{formatMetric(key, value)}
						</TableCell>
						<TableCell className="text-right">
							<MetricDelta
								metricKey={key}
								current={value}
								previous={previous?.[set]?.[key]}
							/>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function InputsTable({
	inputs,
	previous,
}: {
	inputs: NonNullable<NonNullable<AdminScannerModel["reportSummary"]>["inputs"]>
	previous:
		| NonNullable<NonNullable<AdminScannerModel["reportSummary"]>["inputs"]>
		| undefined
}) {
	const previousByName = new Map(
		(previous ?? [])
			.filter((entry) => entry.name)
			.map((entry) => [entry.name, entry.items]),
	)
	return (
		<Table className={FLUSH_TABLE_CLASS}>
			<TableHeader>
				<TableRow>
					<TableHead>Game</TableHead>
					<TableHead className="text-right">Items</TableHead>
					<TableHead className="text-right">Δ prev</TableHead>
					<TableHead>Served by</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{inputs.map((input) => {
					const prevItems = input.name
						? previousByName.get(input.name)
						: undefined
					const delta =
						input.items !== undefined && prevItems !== undefined
							? input.items - prevItems
							: undefined
					return (
						<TableRow key={input.name ?? input.ref ?? JSON.stringify(input)}>
							<TableCell className="font-mono text-xs">{input.name}</TableCell>
							<TableCell className="text-right tabular-nums">
								{input.items ?? "—"}
							</TableCell>
							<TableCell
								className={cn(
									"text-right tabular-nums text-xs",
									delta === undefined || delta === 0
										? "text-meta"
										: delta > 0
											? "text-ok"
											: "text-bad",
								)}
							>
								{delta === undefined
									? ""
									: delta === 0
										? "="
										: delta > 0
											? `+${delta}`
											: `−${Math.abs(delta)}`}
							</TableCell>
							<TableCell className="text-muted-foreground">
								{input.servedBy}
							</TableCell>
						</TableRow>
					)
				})}
			</TableBody>
		</Table>
	)
}

function SignatureRunsMiniGrid({ runs }: { runs: RunRow[] }) {
	const columnDefs = React.useMemo<ColDef<RunRow>[]>(
		() => [
			{
				headerName: "Game",
				field: "gameSlug",
				minWidth: 130,
				cellClass: "font-mono",
			},
			{
				headerName: "Status",
				field: "status",
				minWidth: 120,
				cellRenderer: (p: ICellRendererParams<RunRow>) =>
					p.data ? <StatusBadge status={p.data.status} /> : null,
			},
			{
				headerName: "DB v",
				field: "dbVersion",
				minWidth: 80,
				type: "rightAligned",
				cellClass: "tabular-nums",
				valueFormatter: (p) => (p.value == null ? "—" : String(p.value)),
			},
			{
				headerName: "Finished",
				field: "finishedAt",
				sort: "desc",
				minWidth: 140,
				cellRenderer: (p: ICellRendererParams<RunRow>) =>
					p.data ? <Instant iso={p.data.finishedAt} /> : null,
			},
		],
		[],
	)
	return (
		<ScannerGrid<RunRow>
			columnDefs={columnDefs}
			rowData={runs}
			getRowId={(row) => row.id}
			height={280}
		/>
	)
}
